import clsx from "clsx";
import { useFormContext, type FieldValues } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import InputField from "@medoma/components/InputField/InputField";
import { PlainButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import { hoursToHoursAndMinutes } from "./timeSlotsUtils";
import {
  useFieldErrorWithPrefix,
  useFieldPathWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { useIsInFinalValidationPhase } from "@/forms/AddActivity/TemplateFieldValidationContext";
import type { TemplateTimeSensitivityFields } from "@/forms/AddActivity/templateFormSchema";
import styles from "./TemplateTimeSensitivityPicker.module.scss";

const QuickOptionButtons = ({
  options,
}: {
  options: { label: string; value: string }[];
}) => {
  const { setValue, clearErrors } = useFormContext<FieldValues>();
  const timeSensitivityFieldName =
    useFieldPathWithPrefix<TemplateTimeSensitivityFields>("timeSensitivity");

  return (
    <div className={styles.quickOptionButtons}>
      <Text element="div" size="smallest" color="faded">
        <Trans>Snabbval:</Trans>
      </Text>
      <div className={styles.buttons}>
        {options.map((option, index) => (
          <PlainButton
            key={index}
            onClick={() => {
              setValue(timeSensitivityFieldName, option.value, {
                shouldDirty: true,
              });
              clearErrors(timeSensitivityFieldName);
            }}
            size="small"
          >
            {option.label}
          </PlainButton>
        ))}
      </div>
    </div>
  );
};

export const TemplateTimeSensitivityPicker = ({
  size = "default",
}: {
  size?: "default" | "compact";
}) => {
  const { t } = useLingui();
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<FieldValues>();

  const timeSensitivityFieldName =
    useFieldPathWithPrefix<TemplateTimeSensitivityFields>("timeSensitivity");
  const errorMessage = useFieldErrorWithPrefix<TemplateTimeSensitivityFields>(
    errors,
    "timeSensitivity",
  );
  const isInFinalValidationPhase = useIsInFinalValidationPhase();

  const displayHoursToMinutesConversion =
    parseFloat(watch(timeSensitivityFieldName)?.replace(",", ".")) >= 0;

  return (
    <div
      className={clsx(
        styles.timeSensitivityPicker,
        size === "compact" && styles.compact,
      )}
    >
      <div className={styles.inputAndQuickOptions}>
        <div>
          <InputField
            {...register(timeSensitivityFieldName, {
              required: isInFinalValidationPhase
                ? {
                    value: true,
                    message: t`Du behöver ange en tidsflexibilitet`,
                  }
                : undefined,
              pattern: {
                value: /^0|[1-9]\d*(?:[.,]\d+)?$/,
                message: t`Använd endast siffror och decimaltecken`,
              },
              validate: (value) => {
                if (!value) {
                  return true;
                }
                if (parseFloat(value.replace(",", ".")) >= 24) {
                  return t`Tidsflexibiliteten får inte överstiga 24 timmar`;
                }
                if (parseFloat(value.replace(",", ".")) < 0) {
                  return t`Tidsflexibiliteten kan inte vara mindre än 0 timmar`;
                }
                if (parseFloat(value.replace(",", ".")) === 0) {
                  return t`Tidsflexibiliteten behöver vara mer än 0 timmar`;
                }
                return true;
              },
            })}
            label={t`Ska påbörjas inom`}
            suffix={t`timmar`}
            showOptionalLabel={false}
            size={size}
            outerWidth="fit"
            inputWidth="8ch"
            errorMessage={errorMessage}
          />
          {displayHoursToMinutesConversion ? (
            <Text element="span" size="smallest" color="faded">
              = {hoursToHoursAndMinutes(watch(timeSensitivityFieldName))}
            </Text>
          ) : (
            <Text element="span" size="smallest" color="faded">
              = <Trans>0 timmar</Trans>
            </Text>
          )}
        </div>
        <QuickOptionButtons
          options={[
            { label: t`15 min`, value: "0,25" },
            { label: t`30 min`, value: "0,5" },
            { label: t`1 h`, value: "1" },
            { label: t`2 h`, value: "2" },
            { label: t`3 h`, value: "3" },
            { label: t`4 h`, value: "4" },
            { label: t`5 h`, value: "5" },
            { label: t`8 h`, value: "8" },
          ]}
        />
      </div>
    </div>
  );
};
